import { useState, useEffect, useCallback, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Card,
  CardContent,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import {
  ComposedChart,
  Area,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { useAuthStore } from "../../stores/root_store";
import { taskPaymentApi } from "../../api/task_payment_api";
import type { PaymentBatch } from "../../api/task_payment_api";
import { getPaymentBatchStatusColor } from "./paymentBatchStatus";

function formatDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  return new Date(dateStr).toLocaleDateString();
}

function formatShortDate(dateStr: string | null): string {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`;
}

function getBatchTypeLabel(type: string): string {
  switch (type) {
    case "run":
      return "Regular Payment";
    case "missing":
      return "Missing Fix";
    case "mentor-only":
      return "Mentor Only";
    case "repair-pay-via-shift":
      return "Shift-Flag Fix";
    default:
      return type;
  }
}

interface ChartDataPoint {
  date: string;
  dateLabel: string;
  totalAmount: number;
  userCount: number;
  requestCount: number;
  avgPerUser: number;
  batchType: string;
}

export const PaymentBatchHistoryPage = observer(() => {
  const navigate = useNavigate();
  const authStore = useAuthStore();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<PaymentBatch[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const hasSudo = authStore.roles.includes("sudo");

  const loadBatches = useCallback(async (nextCursor?: string) => {
    try {
      const isLoadMore = !!nextCursor;
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await taskPaymentApi.listBatches(nextCursor, 50);

      if (isLoadMore) {
        setBatches((prev) => [...prev, ...response.items]);
      } else {
        setBatches(response.items);
      }
      setCursor(response.cursor);
      setHasMore(response.has_more);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load batches");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    if (hasSudo) {
      loadBatches();
    }
  }, [hasSudo, loadBatches]);

  const handleLoadMore = () => {
    if (cursor && !loadingMore) {
      loadBatches(cursor);
    }
  };

  const handleRowClick = (batch: PaymentBatch) => {
    navigate(`/manager/task-payment/history/${batch.run_id}`);
  };

  // Prepare chart data - sorted oldest to newest, all completed batches
  const chartData = useMemo((): ChartDataPoint[] => {
    return batches
      .filter((b) => b.status === "completed")
      .map((batch) => ({
        date: batch.date_created,
        dateLabel: formatShortDate(batch.date_created),
        totalAmount: batch.total_amount,
        userCount: batch.user_count,
        requestCount: batch.request_count,
        avgPerUser: batch.avg_per_user,
        batchType: batch.batch_type,
      }))
      .reverse(); // API returns newest first, chart wants oldest first
  }, [batches]);

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    const completedBatches = batches.filter((b) => b.status === "completed");
    if (completedBatches.length === 0) return null;

    const totalPaid = completedBatches.reduce(
      (sum, b) => sum + b.total_amount,
      0
    );
    const totalUsers = completedBatches.reduce(
      (sum, b) => sum + b.user_count,
      0
    );
    const totalRequests = completedBatches.reduce(
      (sum, b) => sum + b.request_count,
      0
    );
    const avgPerBatch = totalPaid / completedBatches.length;

    return {
      totalPaid,
      totalUsers,
      totalRequests,
      avgPerBatch,
      batchCount: completedBatches.length,
    };
  }, [batches]);

  if (!authStore.initialized) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  if (!hasSudo) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          You must have sudo access to view this page.
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/assistant")}
          sx={{ mt: 2 }}
        >
          Back to Dashboard
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate("/manager/task-payment")}
        sx={{ mb: 2 }}
      >
        Back to Task Payment
      </Button>

      <Typography variant="h4" gutterBottom>
        Payment Batch History
      </Typography>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View past payment runs and their aggregate statistics. Click a row to
        see details.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Stats */}
      {summaryStats && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Summary
            </Typography>
            <Box
              sx={{
                display: "flex",
                gap: 4,
                flexWrap: "wrap",
              }}
            >
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Paid
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(summaryStats.totalPaid)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Batches
                </Typography>
                <Typography variant="h5">{summaryStats.batchCount}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Avg per Batch
                </Typography>
                <Typography variant="h5">
                  {formatCurrency(summaryStats.avgPerBatch)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Total Requests
                </Typography>
                <Typography variant="h5">
                  {summaryStats.totalRequests.toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Charts */}
      {!loading && chartData.length > 1 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Payment Trends
            </Typography>

            {/* Total Amount Chart */}
            <Box sx={{ mb: 4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Total Amount per Batch
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={theme.palette.divider}
                  />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                    formatter={(value) => [
                      formatCurrency((value as number) ?? 0),
                      "Total",
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalAmount"
                    fill={theme.palette.primary.main}
                    fillOpacity={0.3}
                    stroke={theme.palette.primary.main}
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            {/* Users and Requests Chart */}
            <Box>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Users & Requests per Batch
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <ComposedChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={theme.palette.divider}
                  />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <YAxis
                    yAxisId="left"
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Legend />
                  <Bar
                    yAxisId="left"
                    dataKey="userCount"
                    name="Users"
                    fill={theme.palette.secondary.main}
                    fillOpacity={0.8}
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="requestCount"
                    name="Requests"
                    fill={theme.palette.success.main}
                    fillOpacity={0.3}
                    stroke={theme.palette.success.main}
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            {/* Average per User Chart */}
            <Box sx={{ mt: 4 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Average Payment per User
              </Typography>
              <ResponsiveContainer width="100%" height={200}>
                <ComposedChart data={chartData}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={theme.palette.divider}
                  />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <YAxis
                    tickFormatter={(value) => `$${value.toFixed(0)}`}
                    tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: theme.palette.background.paper,
                      borderColor: theme.palette.divider,
                    }}
                    formatter={(value) => [
                      formatCurrency((value as number) ?? 0),
                      "Avg/User",
                    ]}
                    labelFormatter={(label) => `Date: ${label}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="avgPerUser"
                    fill={theme.palette.warning.main}
                    fillOpacity={0.3}
                    stroke={theme.palette.warning.main}
                    strokeWidth={2}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Batch Table */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            All Batches
          </Typography>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : batches.length === 0 ? (
            <Alert severity="info">
              No payment batches found. Run a payment to create batch records.
            </Alert>
          ) : (
            <>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Date Range</TableCell>
                      <TableCell align="right">Total</TableCell>
                      <TableCell align="right">Users</TableCell>
                      <TableCell align="right">Requests</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Operator</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {batches.map((batch) => (
                      <TableRow
                        key={batch.run_id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(batch)}
                      >
                        <TableCell>{formatDate(batch.date_created)}</TableCell>
                        <TableCell>
                          {getBatchTypeLabel(batch.batch_type)}
                        </TableCell>
                        <TableCell>
                          {formatDate(batch.date_range_start)} -{" "}
                          {formatDate(batch.date_range_end)}
                        </TableCell>
                        <TableCell align="right">
                          <strong>{formatCurrency(batch.total_amount)}</strong>
                        </TableCell>
                        <TableCell align="right">{batch.user_count}</TableCell>
                        <TableCell align="right">
                          {batch.batch_type === "mentor-only"
                            ? batch.mentor_claim_count
                            : batch.request_count}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={batch.status}
                            color={getPaymentBatchStatusColor(batch.status)}
                            size="small"
                          />
                          {batch.has_errors && (
                            <Chip
                              label="errors"
                              color="error"
                              size="small"
                              sx={{ ml: 0.5 }}
                            />
                          )}
                        </TableCell>
                        <TableCell>{batch.operator_email}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {hasMore && (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? <CircularProgress size={20} /> : "Load More"}
                  </Button>
                </Box>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </Container>
  );
});
